import { useUser } from '@folklore/auth';
import { useResizeObserver } from '@folklore/hooks';
import { useUrlGenerator } from '@folklore/routes';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import { isString } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useWindowSize from '../../hooks/useWindowSize';
import { getPageKey } from '../../lib/utils';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import ModalsContainer from '../modals/Modals';
import Footer from '../partials/Footer';
import Header from '../partials/Header';
import PointsEarned from '../partials/PointsEarned';

import styles from '../../../styles/layouts/main.module.scss';

interface MainLayoutProps {
    page: Page | null;
    loading?: boolean;
    children?: React.ReactNode;
}

function MainLayout({
    page: currentPage = null,
    loading = false,
    children = null,
}: MainLayoutProps) {
    const { locale } = useIntl();
    const { height } = useWindowSize();
    const user = useUser();
    const { points } = user || {};
    const hasUser = user !== null;
    const contest = useContest();
    const url = useUrlGenerator();
    const hasContest = contest !== null;
    const {
        menus = [],
        outcome = {},
        pagesUrls: contestPagesUrls,
        started = false,
        ended = false,
    } = contest || {};
    const { urls: pageUrls } = currentPage || {};
    const theme = useTheme();
    const themeClassName = useThemeClassName(styles, true);
    const { prizes: prizesPageUrl = null } = contestPagesUrls || {};
    const { hasPoints = false, pointsExpirationWeekday = null } = outcome || {};
    const { name: themeName = null, backgroundColor = null } = theme || {};
    const finalBgColor = isString(backgroundColor) ? [backgroundColor] : backgroundColor;
    const { main: mainMenuItems = null, footer: footerMenuItems = null } = menus || {};

    const hasStarted = started || ended;
    const finalMainMenuItems = useMemo(
        () =>
            [
                ...(mainMenuItems !== null
                    ? mainMenuItems.filter(
                          ({ authenticated = false }) => !authenticated || user !== null,
                      )
                    : []),
                user !== null
                    ? {
                          id: 'logout',
                          label: (
                              <FormattedMessage
                                  defaultMessage="Logout"
                                  description="Menu item label"
                              />
                          ),
                          url: url('logout'),
                          external: true,
                          target: '_self',
                      }
                    : null,
            ].filter((it) => it !== null),
        [mainMenuItems, user, url],
    );
    const finalFooterMenuItems = useMemo(
        () =>
            footerMenuItems !== null
                ? footerMenuItems.filter(
                      ({ authenticated = false }) => !authenticated || user !== null,
                  )
                : null,
        [footerMenuItems, user],
    );

    const background = useMemo(
        () =>
            finalBgColor !== null && themeName !== 'nfl'
                ? `${
                      finalBgColor.length === 1
                          ? finalBgColor[0]
                          : `${finalBgColor[0]} linear-gradient(${finalBgColor.join(',')})`
                  }`
                : undefined,
        [finalBgColor],
    );

    const containerRef = useRef(null);
    const {
        entry: { contentRect },
        ref: headerRef,
    } = useResizeObserver();
    const { height: headerHeight = 0 } = contentRect || {};
    useEffect(() => {
        if (containerRef.current !== null) {
            containerRef.current.style.setProperty(
                '--header-calculated-height',
                `${headerHeight}px`,
            );
        }
    }, [headerHeight]);

    const transitions = useTransition(
        [
            {
                page: currentPage,
                children,
            },
        ],
        {
            initial: { opacity: 1, position: 'static' },
            from: { opacity: 0, position: 'static' },
            enter: { opacity: 1, position: 'static' },
            leave: { opacity: 0, position: 'absolute' },
            keys: ({ page }) => getPageKey(page),
        },
    );

    const { handle, type, theme: pageTheme } = currentPage || {};
    const { theme: gameTheme } = 'game' in currentPage ? currentPage.game : {};
    const { fullscreen: pageFullscreen = false } = pageTheme || {};
    const { fullscreen: gameFullcreen = false } = gameTheme || {};

    return (
        <div
            className={classNames([
                styles.container,
                `layout-page-${handle || type}`,
                {
                    [styles.fullscreen]: pageFullscreen || gameFullcreen,
                    [styles.loading]: loading,
                    [styles.withoutContest]: !hasContest,
                    [styles.withoutUser]: user === null,
                },
                themeClassName,
            ])}
            ref={containerRef}
            data-locale={locale}
            style={{ background, minHeight: height }}
        >
            {hasContest && hasStarted ? (
                <Header
                    menuItems={finalMainMenuItems}
                    footerMenuItems={finalFooterMenuItems}
                    pageUrls={pageUrls}
                    ref={headerRef}
                    className={classNames([styles.header, 'layout-header'])}
                />
            ) : null}
            <main className={classNames([styles.main, 'layout-main'])}>
                {hasUser && hasPoints && themeName !== 'defis' && themeName !== 'perfect' ? (
                    <PointsEarned
                        className={styles.pointsAvailable}
                        points={points}
                        expirationWeekday={pointsExpirationWeekday}
                        prizesPageUrl={prizesPageUrl}
                    />
                ) : null}
                {transitions((style, { children: child, page }) => {
                    const { id, handle, type } = page || {};
                    return (
                        <animated.div
                            style={style}
                            className={classNames([
                                styles.page,
                                {
                                    [styles.leaving]: page !== currentPage,
                                },
                                'layout-page',
                                `page-${handle || type}`,
                            ])}
                        >
                            {child}
                        </animated.div>
                    );
                })}
            </main>
            {hasContest && hasStarted ? (
                <Footer
                    className={classNames([styles.footer, 'layout-footer'])}
                    menuItems={finalFooterMenuItems}
                    page={currentPage}
                />
            ) : null}
            <ModalsContainer />
        </div>
    );
}

export default MainLayout;
